'use client';

import React from 'react';
import { BuilderElement } from '@builder.io/react';
import Link from 'next/link';
import { Card, CardContent } from '../ui/card';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import parse from 'html-react-parser';
import { motion, AnimatePresence } from '@/components/LazyMotion';

interface FormSelectorProps {
  title: string;
  content: string;
  form: {
    title: string;
    subtitle: string;
    link: string;
  }[];
  helperText: string;
  infoBoxText: string;
  builderBlock: BuilderElement;
  attributes?: React.HTMLProps<HTMLElement>;
}

export const FormSelector = ({ title, content, form, helperText, infoBoxText }: FormSelectorProps) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-12">
        {title && (
          <h3 className="mb-4">{title}</h3>
        )}
        {content && (
          <div className="paragraph-lg text-muted-foreground">
            {parse(content)}
          </div>
        )}
      </div>

      <Card className="mb-6 p-0 shadow-sm rounded-3xl">
        <CardContent className="p-0">
          {form.map((formItem, index) => (
            <div key={formItem.title}>
              <Link
                href={formItem.link}
                className={cn(
                  "group",
                  "transition-all duration-300 ease-in-out",
                  "w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer",
                  index === 0 && "rounded-t-3xl",
                  index === form.length - 1 && "rounded-b-3xl",
                )}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="flex flex-col">
                  <div className="paragraph-base font-bold">{formItem.title}</div>
                  <AnimatePresence>
                    {formItem.subtitle && hoveredIndex === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="paragraph-sm text-muted-foreground mt-1 overflow-hidden"
                      >
                        {parse(formItem.subtitle)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <Button variant="outline" size="icon" grayBorder>
                  <span className="material-symbols-outlined">arrow_forward</span>
                </Button>
              </Link>
              {index < form.length - 1 && (
                <div className="border-b border-border" />
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {helperText && (
        <div className="mb-12 paragraph-sm text-muted-foreground">
          {parse(helperText)}
        </div>
      )}

      {infoBoxText && (
        <Card className="bg-gray-50 p-0">
          <CardContent className="py-4 px-6 paragraph-sm text-muted-foreground [&_strong]:text-foreground">
            {parse(infoBoxText)}
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 